// src/api/ota.ts

import api from '@/lib/axios';
import type {
  OTAFirmware,
  UploadOTARequest,
  OTADownloadResponse,
} from '@/types/ota';

/**
 * 上传/更新 OTA 固件
 * POST /v1/developer/products/{product_id}/ota
 * @param productId - 产品 ID
 * @param otaData - OTA 数据（version, description）
 * @param file - 固件文件
 * @returns Promise<OTAFirmware> - 返回上传成功的固件信息
 */
export const uploadOTA = (
  productId: string,
  otaData: Pick<UploadOTARequest, 'version' | 'description'>,
  file: File
): Promise<OTAFirmware> => {
  const formData = new FormData();
  formData.append('version', otaData.version);
  if (otaData.description) {
    formData.append('description', otaData.description);
  }
  formData.append('file', file);

  return api.post(`/developer/products/${productId}/ota`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

/**
 * 获取产品 OTA 固件信息
 * GET /v1/developer/products/{product_id}/ota
 * @param productId - 产品 ID
 * @returns Promise<OTAFirmware> - 返回固件信息
 */
export const getOTA = (productId: string): Promise<OTAFirmware> => {
  return api.get(`/developer/products/${productId}/ota`);
};

/**
 * 获取 OTA 下载链接（302 重定向）
 * GET /v1/developer/products/{product_id}/ota/download
 * @param productId - 产品 ID
 * @returns Promise<OTADownloadResponse> - 返回下载信息
 */
export const downloadOTA = (productId: string): Promise<OTADownloadResponse> => {
  return api.get(`/developer/products/${productId}/ota/download`);
};

/**
 * 删除 OTA 固件
 * DELETE /v1/developer/products/{product_id}/ota/{ota_id}
 * @param productId - 产品 ID
 * @param otaId - OTA 固件 ID
 * @returns Promise<void>
 */
export const deleteOTA = (productId: string, otaId: string): Promise<void> => {
  return api.delete(`/developer/products/${productId}/ota/${otaId}`);
};